/**
 * t2v / t2i engines for VisionForge → VideoForge / ImageForge handoff.
 */

import { getAccessToken } from "../suite/auth";
import { loadSuiteSettings, resolveDefaultForgeApiUrl } from "../suite/settings";

export type HandoffKind = "video" | "image";
export type VideoProvider = "any" | "cloudflare" | "hf" | "civitai" | "suite";
export type CivitaiRating = "green" | "red";

export type VideoPick = {
  skuId: string;
  modelId: string;
  name: string;
  provider: "cloudflare" | "hf" | "civitai" | "suite";
  rating: CivitaiRating;
  modality: string;
};

export type ImagePick = VideoPick;

const CACHE_MS = 10 * 60_000;
const CACHE_KEY: Record<HandoffKind, string> = {
  video: "visionforge.videoEngines.v1",
  image: "visionforge.imageEngines.v1",
};

function relayBase(): string {
  const env = (import.meta.env.VITE_RELAY_URL as string | undefined)?.replace(/\/$/, "");
  return env || loadSuiteSettings().forgeApiUrl || resolveDefaultForgeApiUrl();
}

function looksV2v(m: VideoPick): boolean {
  const blob = `${m.modality} ${m.modelId} ${m.name} ${m.skuId}`.toLowerCase();
  return /aleph|in-context video|video-to-video|\bv2v\b/.test(blob);
}

function looksVideo(mod: string): boolean {
  const m = (mod || "").toLowerCase();
  return (
    m.includes("video") ||
    m === "t2v" ||
    m === "i2v" ||
    m.includes("text-to-video") ||
    m.includes("image-to-video")
  );
}

function tabToProvider(tab: string): VideoPick["provider"] {
  if (tab === "hf") return "hf";
  if (tab === "civitai") return "civitai";
  if (tab === "cloudflare") return "cloudflare";
  return "suite";
}

function looksImage(mod: string): boolean {
  const m = (mod || "").toLowerCase();
  return (
    m.includes("image") ||
    m === "t2i" ||
    m.includes("text-to-image") ||
    m.includes("image-to-image")
  );
}

function cacheGet(kind: HandoffKind): VideoPick[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY[kind]);
    if (!raw) return null;
    const j = JSON.parse(raw) as { at?: number; models?: VideoPick[] };
    if (!j.models?.length || !j.at || Date.now() - j.at > CACHE_MS) return null;
    return j.models;
  } catch {
    return null;
  }
}

function cacheSet(kind: HandoffKind, models: VideoPick[]): void {
  try {
    localStorage.setItem(CACHE_KEY[kind], JSON.stringify({ at: Date.now(), models }));
  } catch {
    /* ignore */
  }
}

async function fetchTab(
  tab: string,
  token: string | null,
  kind: HandoffKind,
): Promise<VideoPick[]> {
  const base = relayBase().replace(/\/$/, "");
  const qs = new URLSearchParams({
    tab,
    modality: kind === "image" ? "text-to-image" : "text-to-video",
    app: "visionforge",
  });
  const res = await fetch(`${base}/api/suite/catalog?${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: AbortSignal.timeout(25000),
  });
  if (!res.ok) return [];
  const data = (await res.json()) as {
    models?: Array<Record<string, unknown>>;
  };
  const out: VideoPick[] = [];
  for (const m of data.models || []) {
    const modelId = String(m.modelId || m.skuId || m.id || "");
    const skuId = String(m.skuId || modelId);
    if (!modelId) continue;
    const modality = String(m.modality || (kind === "image" ? "text-to-image" : "text-to-video"));
    const hay = `${m.name || ""} ${modelId} ${m.civitaiType || ""} ${m.baseModel || ""}`.toLowerCase();
    const videoish =
      looksVideo(modality) || /wan|ltx|hunyuan|svd|animatediff|video/.test(hay);
    const imageish =
      looksImage(modality) || /flux|sdxl|sd3|pony|illustrious|checkpoint|lora/.test(hay);
    if (kind === "video") {
      if (tab === "civitai") {
        if (!videoish && !looksVideo(modality)) continue;
      } else if (!looksVideo(modality)) {
        continue;
      }
    } else if (looksVideo(modality) && !looksImage(modality) && !imageish) {
      continue;
    }
    const nsfw = m.nsfw === true || m.rating === "red";
    out.push({
      skuId,
      modelId,
      name: String(m.name || modelId.split("/").pop() || skuId),
      provider: tabToProvider(tab),
      rating: nsfw ? "red" : "green",
      modality,
    });
  }
  return out;
}

export async function fetchHandoffPicks(
  kind: HandoffKind,
  force = false,
): Promise<VideoPick[]> {
  if (!force) {
    const hit = cacheGet(kind);
    if (hit?.length) return hit;
  }
  const token = getAccessToken();
  const chunks = await Promise.allSettled([
    fetchTab("cloudflare", token, kind),
    fetchTab("hf", token, kind),
    fetchTab("civitai", token, kind),
  ]);
  const seen = new Set<string>();
  const models: VideoPick[] = [];
  for (const c of chunks) {
    if (c.status !== "fulfilled") continue;
    for (const m of c.value) {
      if (kind === "video" && looksV2v(m)) continue;
      const key = `${m.provider}:${m.skuId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      models.push(m);
    }
  }
  if (kind === "image") {
    for (const m of [
      {
        skuId: "image-model",
        modelId: "image-model",
        name: "Image Model",
        provider: "suite" as const,
        rating: "green" as const,
        modality: "text-to-image",
      },
      {
        skuId: "image-model-pro",
        modelId: "image-model-pro",
        name: "Image Model Pro",
        provider: "suite" as const,
        rating: "green" as const,
        modality: "text-to-image",
      },
    ]) {
      const key = `${m.provider}:${m.skuId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      models.unshift(m);
    }
  }
  models.sort((a, b) => a.name.localeCompare(b.name));
  if (models.length) cacheSet(kind, models);
  return models;
}

export async function fetchVideoPicks(force = false): Promise<VideoPick[]> {
  return fetchHandoffPicks("video", force);
}

export async function fetchImagePicks(force = false): Promise<ImagePick[]> {
  return fetchHandoffPicks("image", force);
}

export function filterVideoPicks(
  all: VideoPick[],
  opts: {
    provider: VideoProvider;
    rating: CivitaiRating;
    allowRed: boolean;
  },
): VideoPick[] {
  return all.filter((m) => {
    if (opts.provider !== "any" && opts.provider !== "suite" && m.provider !== opts.provider) {
      return false;
    }
    if (opts.provider === "suite" && m.provider === "civitai") return false;
    if (m.provider === "civitai") {
      if (m.rating === "red") return opts.allowRed && opts.rating === "red";
      return opts.rating === "green" || opts.provider !== "civitai";
    }
    return true;
  });
}

export function allowsCivitaiRed(wallet: {
  adultOk?: boolean;
  catalogTabs?: Record<string, boolean>;
} | null): boolean {
  return Boolean(wallet?.adultOk && wallet.catalogTabs?.civitai_red !== false);
}
